import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedSubscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';

import {
  AltOffering,
  AltOfferingCompany,
  OfferingFeedback,
  PaginatedAltOffering,
  OfferingFeedbackResponse,
} from './entities';
import { AltOfferingsRestful } from './restful';
import { FilterOptions } from './store';
import {
  ingestAltOffering,
  ingestAltOfferingCompany,
  ingestAltOfferings,
  PaginatedingestAltOfferings,
} from './ingress';

interface ErrorEvent {
  error: SafeError;
  errorType: 'get_offerings_error' | 'get_listing_error' | 'get_company_error';
  type: 'error';
}

interface GetAltOfferingsEvent {
  offering: AltOffering | AltOffering[];
  type: 'get_offerings' | 'get_listing';
}

interface GetAltOfferingCompanyEvent {
  company: AltOfferingCompany;
  type: 'get_offering_company';
}

export type AltOfferingsRestfulEvent = ErrorEvent | GetAltOfferingsEvent | GetAltOfferingCompanyEvent;

interface AltOfferingsFunctions {
  getCompany: AltOfferingsRequest['getCompany'];
  getListing: AltOfferingsRequest['getListing'];
  getOfferings: AltOfferingsRequest['getOfferings'];
  getReviews: AltOfferingsRequest['getReviews'];
}

export class AltOfferingsRequest extends ExtendedSubscribable<AltOfferingsRestfulEvent, AltOfferingsFunctions> {
  private restful: AltOfferingsRestful;

  constructor(session: Session) {
    super();
    this.restful = new AltOfferingsRestful(session);
  }

  public getOfferings = async (params?: any): SafePromise<AltOffering[]> => {
    const response = await ingestAltOfferings(this.restful.getOfferings(params));
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_offerings_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        offering: response.ok,
        type: 'get_offerings',
      });
    }
    return response;
  };

  public getReviews = async (params?: FilterOptions): SafePromise<PaginatedAltOffering> => {
    const response = await PaginatedingestAltOfferings(this.restful.getReviews(params));

    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_offerings_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        offering: response.ok.offerings,
        type: 'get_offerings',
      });
    }
    return response;
  };

  public getListing = async (listingSlug: string): SafePromise<AltOffering> => {
    const response = await ingestAltOffering(this.restful.getListing(listingSlug));
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_listing_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        offering: response.ok,
        type: 'get_listing',
      });
    }
    return response;
  };

  public submitOfferFeedback = async (
    listingSlug: number | string,
    offeringFeedback: OfferingFeedback,
  ): SafePromise<OfferingFeedbackResponse> => {
    const response = await this.restful.submitOfferFeedback(listingSlug, offeringFeedback);
    return response;
  };

  public getCompany = async (companySlug: string): SafePromise<AltOfferingCompany> => {
    const response = await ingestAltOfferingCompany(this.restful.getCompany(companySlug));
    if (response.err) {
      this.dispatch({
        error: response.err,
        errorType: 'get_company_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        company: response.ok,
        type: 'get_offering_company',
      });
    }
    return response;
  };

  public onSubscribe = (): AltOfferingsFunctions => ({
    getCompany: this.getCompany,
    getListing: this.getListing,
    getOfferings: this.getOfferings,
    getReviews: this.getReviews,
  });
}
